import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { CursorGlow } from "@/components/CursorGlow";
import { Navbar } from "@/components/landing/Navbar";
import { Showreel } from "@/components/landing/Showreel";
import { Manifesto } from "@/components/landing/Manifesto";
import { EditorialMarquee } from "@/components/landing/EditorialMarquee";
import { Services } from "@/components/landing/Services";
import { Portfolio } from "@/components/landing/Portfolio";
import { Contact } from "@/components/landing/Contact";
import { Footer } from "@/components/landing/Footer";
import CaseStudy from "@/pages/CaseStudy";
import Admin from "@/pages/Admin";

const Home = () => (
  <>
    <Navbar />
    <main>
      <Showreel />
      <Manifesto />
      <EditorialMarquee />
      <Services />
      <Portfolio />
      <Contact />
    </main>
    <Footer />
  </>
);

// Keeps a global `navigate` handle (for lib/scroll.js) and smooth-scrolls to
// a hash target after any client-side route change (e.g. a case study CTA
// sending the visitor back to "/#work" — no full page reload).
const ScrollManager = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    window.__navigate = navigate;
  }, [navigate]);

  useEffect(() => {
    if (location.pathname !== "/" || !location.hash) return;
    const id = location.hash.slice(1);
    let cancelled = false;
    let rafId;
    let lastTop = null;
    let stableFrames = 0;
    let elapsedFrames = 0;

    const goTo = (el) => {
      const lenis = window.__lenis;
      if (lenis) lenis.scrollTo(el, { offset: -20, duration: 1.1 });
      else el.scrollIntoView({ behavior: "smooth" });
    };

    // Wait for the target's position to stop moving (below-the-fold images,
    // the marquee, etc. can still be settling their layout right after the
    // Home page mounts) before animating — otherwise we scroll to a stale Y.
    const waitForStableLayout = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (!el) {
        rafId = requestAnimationFrame(waitForStableLayout);
        return;
      }
      const top = el.getBoundingClientRect().top;
      const stable = lastTop !== null && Math.abs(top - lastTop) < 1;
      stableFrames = stable ? stableFrames + 1 : 0;
      lastTop = top;
      elapsedFrames += 1;
      // 6 stable frames in a row, or a 90-frame (~1.5s) hard cap either way.
      if (stableFrames >= 6 || elapsedFrames >= 90) {
        goTo(el);
        // One corrective pass once any late reflow (images, marquee) has
        // fully settled, in case the first scroll landed slightly short.
        setTimeout(() => {
          if (!cancelled) {
            const el2 = document.getElementById(id);
            if (el2) goTo(el2);
          }
        }, 900);
        return;
      }
      rafId = requestAnimationFrame(waitForStableLayout);
    };

    rafId = requestAnimationFrame(waitForStableLayout);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [location.pathname, location.hash]);

  return null;
};

function App() {
  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;

    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <div className="App">
        <div className="grain-overlay" />
        <CursorGlow />
        <ScrollManager />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/work/:slug" element={<CaseStudy />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<Home />} />
        </Routes>
        <Toaster position="bottom-right" theme="dark" />
      </div>
    </BrowserRouter>
  );
}

export default App;
